import { appConfig } from './config';
import { Instant } from '@js-joda/core';
import { InstantCodec } from '@thinkalpha/common/schemas/codecs.js';
import { BardexCookieRequestQueryString, BardexCookieResponse } from '@thinkalpha/platform-ws-client/contracts/data.js';
import axios from 'axios';
import { addRequestIdInterceptor, api } from 'src/api';
import { staticContainer } from 'src/StaticContainer';
import { z } from 'zod';

const inst = axios.create({ baseURL: appConfig.bardexApi, paramsSerializer: { indexes: false } });
inst.interceptors.request.use(...addRequestIdInterceptor);

/*
SAMPLE PAYLOAD:

{
    "symbols": [
        "AAPL",
        "MSFT"
    ],
    "timeframe": {
        "start": "2024-01-01T00:00:00Z",
        "end": "2024-12-31T23:59:59Z"
    },
    "barLength": "1d",
    "bars": [
        "BarHigh",
        "BarLow",
        "BarOpen",
        "BarClose",
        "BarVolume"
    ]
}
*/

export const BarsRequest = z.object({
    symbols: z.array(z.string()),
    timeframe: z.object({
        start: InstantCodec,
        end: InstantCodec,
    }),
    barLength: z.string(),
    bars: z.array(z.string()),
    countBack: z.number().optional(),
    sessionId: z.string().optional(),
    sessionWin: z.string().optional(),
    sessionTz: z.string().optional(),
});
export type BarsRequest = z.infer<typeof BarsRequest>;

const log = staticContainer.get('Logger').getSubLogger({ name: 'BardexService' });

export async function getBardexToken(request: BardexCookieRequestQueryString) {
    const params = BardexCookieRequestQueryString.encode(request);
    if (TRACE) {
        log.trace('Getting Bardex access cookie with request:', { request, params });
    }
    try {
        const res = await api.get<unknown>('/data-requests/bardex-access-cookie', {
            params,
        });
        const respPayload = BardexCookieResponse.parse(res.data);

        if (TRACE) {
            log.trace('Received Bardex access cookie response:', { respPayload });
        }
        return respPayload;
    } catch (error) {
        log.error('Error getting Bardex access cookie', { error, request, params });
        throw error;
    }
}

export interface BardexBarPoint {
    symbol: string;
    unixTime: number;
    bars: Record<string, number>;
}

/**
 * Parses a unix time from a DataView at the given offset.
 */
export function getBardexUnixTime(view: DataView, offset: number): number {
    const timeMsLow = view.getUint32(offset, true);
    const timeMsHigh = view.getInt32(offset + 4, true);
    const timeMs = timeMsHigh * 0x100000000 + (timeMsLow >>> 0);
    return timeMs;
}

export async function* getBars(request: BarsRequest, cookie: string): AsyncGenerator<BardexBarPoint> {
    if (TRACE) {
        log.trace('Requesting Bardex bars with request:', { request });
    }
    const res = await inst.post('/queries/tv', request, {
        responseType: 'arraybuffer',
        headers: { Authorization: `Cookie ${cookie}`, 'Content-Type': 'application/json' },
    });
    const data: ArrayBuffer = res.data;
    const iterator = parseBarsFromBinaryData(data, request);
    yield* iterateBarsWithLogging(iterator, request);
}

export function* iterateBarsWithLogging(
    iterator: Iterator<BardexBarPoint>,
    request: BarsRequest,
): Generator<BardexBarPoint> {
    let count: number = 0;
    let firstTimestamp: Instant | undefined;
    let lastTimestamp: Instant | undefined;

    let barPoint: BardexBarPoint;
    let bar = iterator.next();
    if (!bar.done) {
        barPoint = bar.value;
        firstTimestamp = Instant.ofEpochMilli(barPoint.unixTime);

        // now start the rest of the iteration
        do {
            barPoint = bar.value;
            yield barPoint;

            count++;
            bar = iterator.next();
        } while (!bar.done);
        lastTimestamp = Instant.ofEpochMilli(barPoint.unixTime);
    }
    if (request.countBack && count < request.countBack) {
        log.warn(
            `Requested countBack of ${request.countBack} but only received ${count} bars from ${firstTimestamp} to ${lastTimestamp}.`,
            {
                request,
                count,
                firstTimestamp: firstTimestamp?.toString(),
                firstRequestedTimestamp: request.timeframe.start.toString(),
                lastTimestamp: lastTimestamp?.toString(),
                lastRequestedTimestamp: request.timeframe.end.toString(),
                countBack: request.countBack,
            },
        );
    } else if (TRACE) {
        log.trace(`Received ${count} bars from ${firstTimestamp} to ${lastTimestamp} for request.`, {
            request,
            count,
            firstTimestamp: firstTimestamp?.toString(),
            firstRequestedTimestamp: request.timeframe.start.toString(),
            lastTimestamp: lastTimestamp?.toString(),
            lastRequestedTimestamp: request.timeframe.end.toString(),
            countBack: request.countBack,
        });
    }
}

export function* parseBarsFromBinaryData(data: ArrayBuffer, request: BarsRequest): Generator<BardexBarPoint> {
    const dataView = new DataView(data);

    /**
     * Object to be reused over and over and sent to the caller
     */
    const barObject: BardexBarPoint = { symbol: 'tbd', unixTime: 0, bars: {} };
    const barRecordSize =
        8 /* unixTime */ +
        1 /* symbol index */ +
        7 /* byte alignment */ +
        request.bars.length * 8; /* each bar as float64 */

    for (let offset = 0; offset + barRecordSize <= dataView.byteLength /* ; offset += barRecordSize */; ) {
        barObject.unixTime = getBardexUnixTime(dataView, offset);
        offset += 8;
        const symbolIndex = dataView.getUint8(offset);
        barObject.symbol = request.symbols[symbolIndex];
        offset += 1 + 7; // symbol index + byte alignment

        for (let i = 0; i < request.bars.length; i++) {
            const barValue = dataView.getFloat64(offset, true);
            barObject.bars[request.bars[i]] = barValue;
            offset += 8;
        }

        yield barObject;
    }
}
